import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Week 15: A tutorial on the expressiveness and universality of fold",
  "description": "",
  "published": "2014-02-13T00:00:00.000Z",
  "redirect_from": ["/blog/week-15-a-tutorial-on-the-expresiveness-and-universality-of-fold/swizec/6514"],
  "categories": "52papers52weeks, Learning, Personal",
  "hero": "./img/images-8825.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`[`}{`This post is part of an ongoing challenge to understand 52 papers in 52 weeks. You can read previous entries, `}<a parentName="em" {...{
          "href": "http://swizec.com/blog/category/52-papers-in-52-weeks"
        }}>{`here`}</a>{`, or subscribe to be notified of new posts by `}<a parentName="em" {...{
          "href": "http://swiz.ec/52papers-list"
        }}>{`email`}</a>{`]`}</em></p>
    <p>{`Fold or foldr is a recursion operator with magical properties commonly used in functional programming. Sometimes known as `}<em parentName="p">{`reduce`}</em>{`, `}<em parentName="p">{`inject`}</em>{`, or `}<em parentName="p">{`compress`}</em>{` it turns a list of values into a single value according to a combining function.`}</p>
    <p>{`Graham Hutton's 1999 `}<a parentName="p" {...{
        "href": "https://docs.google.com/viewer?url=http%3A%2F%2Fwww.cs.nott.ac.uk%2F%7Egmh%2Ffold.pdf"
      }}><em parentName="a">{`A tutorial on the expressivenes nad universality of fold`}</em></a>{` does a great job of explaining just how magical it is. In a language with first-order tuples and functions any function can be expressed as a `}<em parentName="p">{`fold`}</em>{`, it lets you prove programs without using induction, and you can even generalise it to work on datatypes other than lists.`}</p>
    <p>{`Great magic indeed. But with great power comes great responsibility. Take heed of the lesser programmer and remember that many functions you rewrite as a `}<em parentName="p">{`fold`}</em>{`, you will not be able to read in six months.`}</p>
    <h2 {...{
      "id": "the-fold-operator"
    }}>{`The fold operator`}</h2>
    <p>{`Fold as a central concept in a programming language dates from `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/APL_%28programming_language%29",
        "title": "APL (programming language)"
      }}>{`APL`}</a>{`'s reduction operator in 1962 and later `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`FP`}</a>{`'s insertion operator in 1978. In `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{` it's defined like this:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/1061778b3f3b172a2311d473f113f284/a13c9/images-8825.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "16.143497757847534%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'65\\'%20viewBox=\\'0%200%20400%2065\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1061778b3f3b172a2311d473f113f284/ca0a1/images-8825.webp 223w", "/static/1061778b3f3b172a2311d473f113f284/75680/images-8825.webp 445w", "/static/1061778b3f3b172a2311d473f113f284/8d1ba/images-8825.webp 890w", "/static/1061778b3f3b172a2311d473f113f284/3126b/images-8825.webp 1178w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1061778b3f3b172a2311d473f113f284/e92b6/images-8825.png 223w", "/static/1061778b3f3b172a2311d473f113f284/e66bf/images-8825.png 445w", "/static/1061778b3f3b172a2311d473f113f284/4ef49/images-8825.png 890w", "/static/1061778b3f3b172a2311d473f113f284/a13c9/images-8825.png 1178w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/1061778b3f3b172a2311d473f113f284/4ef49/images-8825.png",
                "alt": "Fold in Haskell",
                "title": "Fold in Haskell",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Fold in Haskell`}</figcaption>{`
  `}</figure></p>
    <p>{`The paper uses Haskell for all its examples so I'm going to stick to that as well.`}</p>
    <p>{`This code tells us that `}<em parentName="p">{`fold`}</em>{` takes a function `}<inlineCode parentName="p">{`f`}</inlineCode>{` of type α → β → β, a value `}<inlineCode parentName="p">{`v`}</inlineCode>{` of type β, and processes a list of type `}{`[`}{`α] to return a value of type β. It does this by replacing the nil constructor `}{`[`}{`] with `}<inlineCode parentName="p">{`v`}</inlineCode>{`. And every cons operator `}<inlineCode parentName="p">{`:`}</inlineCode>{` with the function `}<inlineCode parentName="p">{`f`}</inlineCode>{`.`}</p>
    <p>{`Just think of lists as long chains of cons followed by a nil. You can think of a list `}<inlineCode parentName="p">{`[1,2,3]`}</inlineCode>{` as prepending each value to an existing list - `}<inlineCode parentName="p">{`1:2:3:[]`}</inlineCode>{`.`}</p>
    <p>{`When you apply a 'fold (+) 0' it becomes '1+2+3+0', which just so happens to be the sum function. But that's essentially all that fold does - it replaces the cons in lists with a supplied function and the nil constructor with a base value.`}</p>
    <p>{`There's also a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Fold_%28higher-order_function%29",
        "title": "Fold (higher-order function)"
      }}>{`right fold`}</a>{`, `}<em parentName="p">{`foldr`}</em>{`, which does the same, but right-to-left. Using foldl vs. foldr changes the associativity of the supplied function:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`Prelude> foldl (-) 0 [1,2,3] # 0-1-2-3
-6
Prelude> foldr (-) 0 [1,2,3] # 1-(2-(3-0))
2
`}</code></pre>
    <p>{`We can implement all the standard functions using fold using more complex functions. Actually we can implement anything, but we'll get to that. Here's a `}<em parentName="p">{`length`}</em>{` example that tells us the length of a list.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "826px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/c4de11bd95aa163a75bfb371eac5870a/6a6e9/images-8831.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "16.143497757847534%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'65\\'%20viewBox=\\'0%200%20400%2065\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/c4de11bd95aa163a75bfb371eac5870a/ca0a1/images-8831.webp 223w", "/static/c4de11bd95aa163a75bfb371eac5870a/75680/images-8831.webp 445w", "/static/c4de11bd95aa163a75bfb371eac5870a/40616/images-8831.webp 826w"],
                "sizes": "(max-width: 826px) 100vw, 826px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/c4de11bd95aa163a75bfb371eac5870a/e92b6/images-8831.png 223w", "/static/c4de11bd95aa163a75bfb371eac5870a/e66bf/images-8831.png 445w", "/static/c4de11bd95aa163a75bfb371eac5870a/6a6e9/images-8831.png 826w"],
                "sizes": "(max-width: 826px) 100vw, 826px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/c4de11bd95aa163a75bfb371eac5870a/6a6e9/images-8831.png",
                "alt": "Length implemented using fold",
                "title": "Length implemented using fold",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Length implemented using fold`}</figcaption>{`
  `}</figure></p>
    <p>{`You see, instead of transforming the string, that lambda function just counts.`}</p>
    <h2 {...{
      "id": "the-universal-property-of-fold"
    }}>{`The `}<a parentName="h2" {...{
        "href": "http://en.wikipedia.org/wiki/Universal_property",
        "title": "Universal property"
      }}>{`universal property`}</a>{` of fold`}</h2>
    <p>{`In 1990 Malcolm was the first to systematically use the universal property of fold in his generalisation of lists to arbitrary datatypes. For finite lists it can be stated as an equivalence between two definitions of `}<inlineCode parentName="p">{`g`}</inlineCode>{`.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/927ceedff4d3ee673456631e39e57aa7/c83ae/images-8867.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "11.659192825112108%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'47\\'%20viewBox=\\'0%200%20400%2047\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/927ceedff4d3ee673456631e39e57aa7/ca0a1/images-8867.webp 223w", "/static/927ceedff4d3ee673456631e39e57aa7/75680/images-8867.webp 445w", "/static/927ceedff4d3ee673456631e39e57aa7/8d1ba/images-8867.webp 890w", "/static/927ceedff4d3ee673456631e39e57aa7/2baf0/images-8867.webp 1180w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/927ceedff4d3ee673456631e39e57aa7/e92b6/images-8867.png 223w", "/static/927ceedff4d3ee673456631e39e57aa7/e66bf/images-8867.png 445w", "/static/927ceedff4d3ee673456631e39e57aa7/4ef49/images-8867.png 890w", "/static/927ceedff4d3ee673456631e39e57aa7/c83ae/images-8867.png 1180w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/927ceedff4d3ee673456631e39e57aa7/4ef49/images-8867.png",
                "alt": "Universal property",
                "title": "Universal property",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Universal property`}</figcaption>{`
  `}</figure></p>
    <p>{`Looking left-to-right, substituting `}<inlineCode parentName="p">{`g`}</inlineCode>{` with `}<inlineCode parentName="p">{`g = fold f v`}</inlineCode>{` gives you the recursive definition of `}<em parentName="p">{`fold`}</em>{`, and right-to-left the two equations for `}<inlineCode parentName="p">{`g`}</inlineCode>{` are exactly the assumptions to inductively prove that `}<em parentName="p">{`g`}</em>{` equals `}<em parentName="p">{`fold f v`}</em>{`.`}</p>
    <p>{`The universal property is particularly useful as a `}<strong parentName="p">{`proof principle`}</strong>{` because it encapsulates a common pattern of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Mathematical_induction",
        "title": "Mathematical induction"
      }}>{`inductive proof`}</a>{`. Rather than mess about with induction, you can just appeal to the universal property.`}</p>
    <p>{`Let's prove a simple equivalence:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`(+1) . sum = fold (+) 1
`}</code></pre>
    <p>{`The left function is a composition of `}<inlineCode parentName="p">{`+1`}</inlineCode>{` and `}<inlineCode parentName="p">{`sum`}</inlineCode>{`, which sums a list then increments the result. The right function replaces every cons in a list with a `}<inlineCode parentName="p">{`+`}</inlineCode>{` and the empty list with a `}<inlineCode parentName="p">{`1`}</inlineCode>{`.`}</p>
    <p>{`We want to prove they will always give the same result when applied to the same list.`}</p>
    <p>{`The equation matches the right-hand side of the universal property of fold, `}<inlineCode parentName="p">{`g = fold f v`}</inlineCode>{`, with `}<inlineCode parentName="p">{`g = (+1) . sum`}</inlineCode>{`, `}<inlineCode parentName="p">{`f = (+)`}</inlineCode>{`, and `}<inlineCode parentName="p">{`v = 1`}</inlineCode>{`. With some simplification, the equation is equivalent to these:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`sum   []   + 1 = 1
sum (x:xs) + 1 = x + (sum xs + 1)
`}</code></pre>
    <p>{`Which can be verified by simple calculation:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/40691eccb97cba2d8335af868dce2368/d0ab7/images-8945.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "21.524663677130047%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'86\\'%20viewBox=\\'0%200%20400%2086\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/40691eccb97cba2d8335af868dce2368/ca0a1/images-8945.webp 223w", "/static/40691eccb97cba2d8335af868dce2368/75680/images-8945.webp 445w", "/static/40691eccb97cba2d8335af868dce2368/8d1ba/images-8945.webp 890w", "/static/40691eccb97cba2d8335af868dce2368/3838e/images-8945.webp 1335w", "/static/40691eccb97cba2d8335af868dce2368/26719/images-8945.webp 1532w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/40691eccb97cba2d8335af868dce2368/e92b6/images-8945.png 223w", "/static/40691eccb97cba2d8335af868dce2368/e66bf/images-8945.png 445w", "/static/40691eccb97cba2d8335af868dce2368/4ef49/images-8945.png 890w", "/static/40691eccb97cba2d8335af868dce2368/4e814/images-8945.png 1335w", "/static/40691eccb97cba2d8335af868dce2368/d0ab7/images-8945.png 1532w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/40691eccb97cba2d8335af868dce2368/4ef49/images-8945.png",
                "alt": "Proof of sum = fold",
                "title": "Proof of sum = fold",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Proof of sum = fold`}</figcaption>{`
  `}</figure></p>
    <p>{`This concludes the proof. Much simpler than explicitly performing induction!`}</p>
    <p>{`For a less haskelly example, here's what happened:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var inc = function (x) { return x+1; }
var sum = function (x, xs) {
 return xs.length > 0 ? x + sum(xs[0], xs.slice(1)) : x;
}

var plus = function (a, b) { return a+b; }

// we try to prove this equivalence, this is NOT something you execute :)
function (xs) { return inc(sum(0, xs)); } == function (xs) { return xs.reduce(plus, 1); }

// and then the same proof applies
`}</code></pre>
    <p>{`The `}<strong parentName="p">{`fusion property of fold`}</strong>{` allows us to fuse the composition of an arbitrary function and `}<em parentName="p">{`fold`}</em>{` into a single `}<em parentName="p">{`fold`}</em>{`. This isn't true in general, so we use the universal property to calculate the conditions.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`h . fold g w = fold f v
`}</code></pre>
    <p>{`Using the same process as above, we expand this equation into two equations.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`h (fold g w   [])   = v
h (fold g w (x:xs)) = f x (h (fold g w xs))
`}</code></pre>
    <p>{`Which we simplify with two calculations:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/37048/images-8947.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "47.53363228699552%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'191\\'%20viewBox=\\'0%200%20400%20191\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/ca0a1/images-8947.webp 223w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/75680/images-8947.webp 445w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/8d1ba/images-8947.webp 890w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/3838e/images-8947.webp 1335w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/35b63/images-8947.webp 1352w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/e92b6/images-8947.png 223w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/e66bf/images-8947.png 445w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/4ef49/images-8947.png 890w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/4e814/images-8947.png 1335w", "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/37048/images-8947.png 1352w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/7b6b0cde8b67b6d46db0ec0fe33c8a54/4ef49/images-8947.png",
              "alt": "images 8947",
              "title": "images 8947",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`To get:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/e8e83239fffea735389062ea37d5e64d/6b95e/images-8948.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "9.417040358744396%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'38\\'%20viewBox=\\'0%200%20400%2038\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e8e83239fffea735389062ea37d5e64d/ca0a1/images-8948.webp 223w", "/static/e8e83239fffea735389062ea37d5e64d/75680/images-8948.webp 445w", "/static/e8e83239fffea735389062ea37d5e64d/8d1ba/images-8948.webp 890w", "/static/e8e83239fffea735389062ea37d5e64d/3838e/images-8948.webp 1335w", "/static/e8e83239fffea735389062ea37d5e64d/bb4a2/images-8948.webp 1458w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/e8e83239fffea735389062ea37d5e64d/e92b6/images-8948.png 223w", "/static/e8e83239fffea735389062ea37d5e64d/e66bf/images-8948.png 445w", "/static/e8e83239fffea735389062ea37d5e64d/4ef49/images-8948.png 890w", "/static/e8e83239fffea735389062ea37d5e64d/4e814/images-8948.png 1335w", "/static/e8e83239fffea735389062ea37d5e64d/6b95e/images-8948.png 1458w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/e8e83239fffea735389062ea37d5e64d/4ef49/images-8948.png",
                "alt": "Conditions for equivalence",
                "title": "Conditions for equivalence",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Conditions for equivalence`}</figcaption>{`
  `}</figure></p>
    <p>{`Which basically says that, if the conditions on the left are met, then our composition of an arbitrary function and fold behaves exactly as a fold would.`}</p>
    <p>{`Now we can greatly simplify our proof of the sum equation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`(+1) . sum = fold (+) 1
=>
(+1) . fold (+) 0 = fold (+) 1
`}</code></pre>
    <p>{`Which matches the fusion property and we're done. Simple :)`}</p>
    <p>{`We can also use universality as a `}<strong parentName="p">{`definition principle`}</strong>{` to help us transform explicit recursion into folds.`}</p>
    <p>{`Let's take a recursively defined sum:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "738px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/76a3eb38c43a398f248420284ddf5257/774b6/images-8950.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "23.76681614349776%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'95\\'%20viewBox=\\'0%200%20400%2095\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M241%2039c-4%204-1%2016%204%2016%204%200%207-11%204-16-1-2-6-3-8%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/76a3eb38c43a398f248420284ddf5257/ca0a1/images-8950.webp 223w", "/static/76a3eb38c43a398f248420284ddf5257/75680/images-8950.webp 445w", "/static/76a3eb38c43a398f248420284ddf5257/65afa/images-8950.webp 738w"],
                "sizes": "(max-width: 738px) 100vw, 738px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/76a3eb38c43a398f248420284ddf5257/e92b6/images-8950.png 223w", "/static/76a3eb38c43a398f248420284ddf5257/e66bf/images-8950.png 445w", "/static/76a3eb38c43a398f248420284ddf5257/774b6/images-8950.png 738w"],
                "sizes": "(max-width: 738px) 100vw, 738px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/76a3eb38c43a398f248420284ddf5257/774b6/images-8950.png",
                "alt": "Recursively defined sum",
                "title": "Recursively defined sum",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Recursively defined sum`}</figcaption>{`
  `}</figure></p>
    <p>{`We want to rewrite this into a `}<em parentName="p">{`fold`}</em>{`. That is, we're going to solve the equation `}<inlineCode parentName="p">{`sum = fold f v`}</inlineCode>{` for function `}<inlineCode parentName="p">{`f`}</inlineCode>{` and value `}<inlineCode parentName="p">{`v`}</inlineCode>{`. Because it matches the right-hand side of the universal property, we turn it into two equations:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "752px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/92cf970d0a2584de17df937190f64649/442cb/images-8951.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "17.040358744394617%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'68\\'%20viewBox=\\'0%200%20400%2068\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/92cf970d0a2584de17df937190f64649/ca0a1/images-8951.webp 223w", "/static/92cf970d0a2584de17df937190f64649/75680/images-8951.webp 445w", "/static/92cf970d0a2584de17df937190f64649/8cb39/images-8951.webp 752w"],
              "sizes": "(max-width: 752px) 100vw, 752px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/92cf970d0a2584de17df937190f64649/e92b6/images-8951.png 223w", "/static/92cf970d0a2584de17df937190f64649/e66bf/images-8951.png 445w", "/static/92cf970d0a2584de17df937190f64649/442cb/images-8951.png 752w"],
              "sizes": "(max-width: 752px) 100vw, 752px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/92cf970d0a2584de17df937190f64649/442cb/images-8951.png",
              "alt": "images 8951",
              "title": "images 8951",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`We can see from the first equation that `}<inlineCode parentName="p">{`v = 0`}</inlineCode>{`. From the second equation we calculate a definition for `}<inlineCode parentName="p">{`f`}</inlineCode>{`.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/ba8fc57072aab6cecdf90496c21b5848/69476/images-8952.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "47.53363228699552%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'191\\'%20viewBox=\\'0%200%20400%20191\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M114%2031v12c-3%200-1%202%202%202%2010%200%2015-10%207-14h-9\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/ba8fc57072aab6cecdf90496c21b5848/ca0a1/images-8952.webp 223w", "/static/ba8fc57072aab6cecdf90496c21b5848/75680/images-8952.webp 445w", "/static/ba8fc57072aab6cecdf90496c21b5848/8d1ba/images-8952.webp 890w", "/static/ba8fc57072aab6cecdf90496c21b5848/dafe9/images-8952.webp 926w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/ba8fc57072aab6cecdf90496c21b5848/e92b6/images-8952.png 223w", "/static/ba8fc57072aab6cecdf90496c21b5848/e66bf/images-8952.png 445w", "/static/ba8fc57072aab6cecdf90496c21b5848/4ef49/images-8952.png 890w", "/static/ba8fc57072aab6cecdf90496c21b5848/69476/images-8952.png 926w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/ba8fc57072aab6cecdf90496c21b5848/4ef49/images-8952.png",
              "alt": "images 8952",
              "title": "images 8952",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`We calculated that `}<inlineCode parentName="p">{`f = (+)`}</inlineCode>{` and `}<inlineCode parentName="p">{`sum = fold (+) 0`}</inlineCode>{`, which is a contrived example because the definition using fold is obvious, but does a good job of showing the idea behind what we're doing.`}</p>
    <p>{`The key step (†) above is the generalisation of `}<inlineCode parentName="p">{`sum xs`}</inlineCode>{` to a fresh variable `}<inlineCode parentName="p">{`y`}</inlineCode>{`. This step will feature in most such calculations and isn't specific to `}<em parentName="p">{`sum`}</em>{`.`}</p>
    <p>{`Using this process, you can even write `}<em parentName="p">{`map`}</em>{` as a `}<em parentName="p">{`fold`}</em>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`map f = fold (λx ys → f x : ys) []
`}</code></pre>
    <h2 {...{
      "id": "increasing-the-power-of-fold-with-tuples"
    }}>{`Increasing the power of fold with tuples`}</h2>
    <p>{`We can make `}<em parentName="p">{`fold`}</em>{` even more powerful by using it to generate tuples. Say you want a sumlength function that returns a list's length as well as its sum:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`sumlength   :: [Int] → (Int, Int)
sumlength xs = (sum xs, length xs)
`}</code></pre>
    <p>{`Turning this function into a single `}<em parentName="p">{`fold`}</em>{` is more efficient because it only makes a single pass over the list.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`sumlength = fold (λn (x, y) → (n+x, 1+y)) (0, 0)
`}</code></pre>
    <p>{`In fact, any pair of applications of fold to the same list can be rewritten as a single fold. Even a function such as `}<em parentName="p">{`dropWhile`}</em>{`, which drops elements from a list as long as a predicate holds true.`}</p>
    <p>{`First we need a sister function, `}<em parentName="p">{`dropWhile'`}</em>{`, that pairs the argument list together with the result:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/55fa96724772aec1818aecce84c1c1be/e72de/images-8954.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "12.556053811659194%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'51\\'%20viewBox=\\'0%200%20400%2051\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/55fa96724772aec1818aecce84c1c1be/ca0a1/images-8954.webp 223w", "/static/55fa96724772aec1818aecce84c1c1be/75680/images-8954.webp 445w", "/static/55fa96724772aec1818aecce84c1c1be/8d1ba/images-8954.webp 890w", "/static/55fa96724772aec1818aecce84c1c1be/40d0b/images-8954.webp 1198w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/55fa96724772aec1818aecce84c1c1be/e92b6/images-8954.png 223w", "/static/55fa96724772aec1818aecce84c1c1be/e66bf/images-8954.png 445w", "/static/55fa96724772aec1818aecce84c1c1be/4ef49/images-8954.png 890w", "/static/55fa96724772aec1818aecce84c1c1be/e72de/images-8954.png 1198w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/55fa96724772aec1818aecce84c1c1be/4ef49/images-8954.png",
                "alt": "dropWhile'",
                "title": "dropWhile'",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`dropWhile'`}</figcaption>{`
  `}</figure></p>
    <p>{`Which, as usual, we decide is equivalent to these two equations according to the universal property:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/bf43fc48005f03368602876fbf25e541/6937a/images-8955.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "13.004484304932737%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'53\\'%20viewBox=\\'0%200%20400%2053\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/bf43fc48005f03368602876fbf25e541/ca0a1/images-8955.webp 223w", "/static/bf43fc48005f03368602876fbf25e541/75680/images-8955.webp 445w", "/static/bf43fc48005f03368602876fbf25e541/8d1ba/images-8955.webp 890w", "/static/bf43fc48005f03368602876fbf25e541/e4bbf/images-8955.webp 1094w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/bf43fc48005f03368602876fbf25e541/e92b6/images-8955.png 223w", "/static/bf43fc48005f03368602876fbf25e541/e66bf/images-8955.png 445w", "/static/bf43fc48005f03368602876fbf25e541/4ef49/images-8955.png 890w", "/static/bf43fc48005f03368602876fbf25e541/6937a/images-8955.png 1094w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/bf43fc48005f03368602876fbf25e541/4ef49/images-8955.png",
              "alt": "images 8955",
              "title": "images 8955",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`It's obvious from the first equation that `}<inlineCode parentName="p">{`v = ([], [])`}</inlineCode>{`. We get the definition for `}<inlineCode parentName="p">{`f`}</inlineCode>{` from a calculation like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/9acdae61cd919b04721ca548320633a5/f1c64/images-8956.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "36.32286995515695%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'144\\'%20viewBox=\\'0%200%20400%20144\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/9acdae61cd919b04721ca548320633a5/ca0a1/images-8956.webp 223w", "/static/9acdae61cd919b04721ca548320633a5/75680/images-8956.webp 445w", "/static/9acdae61cd919b04721ca548320633a5/8d1ba/images-8956.webp 890w", "/static/9acdae61cd919b04721ca548320633a5/3838e/images-8956.webp 1335w", "/static/9acdae61cd919b04721ca548320633a5/52977/images-8956.webp 1390w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/9acdae61cd919b04721ca548320633a5/e92b6/images-8956.png 223w", "/static/9acdae61cd919b04721ca548320633a5/e66bf/images-8956.png 445w", "/static/9acdae61cd919b04721ca548320633a5/4ef49/images-8956.png 890w", "/static/9acdae61cd919b04721ca548320633a5/4e814/images-8956.png 1335w", "/static/9acdae61cd919b04721ca548320633a5/f1c64/images-8956.png 1390w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/9acdae61cd919b04721ca548320633a5/4ef49/images-8956.png",
              "alt": "images 8956",
              "title": "images 8956",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`And finally our `}<inlineCode parentName="p">{`dropWhile'`}</inlineCode>{` function looks like this:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/01a4a62b9eb69a808ae00f08d04dbfdc/5a791/images-8958.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "20.62780269058296%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'83\\'%20viewBox=\\'0%200%20400%2083\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/01a4a62b9eb69a808ae00f08d04dbfdc/ca0a1/images-8958.webp 223w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/75680/images-8958.webp 445w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/8d1ba/images-8958.webp 890w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/1b8e7/images-8958.webp 1248w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/01a4a62b9eb69a808ae00f08d04dbfdc/e92b6/images-8958.png 223w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/e66bf/images-8958.png 445w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/4ef49/images-8958.png 890w", "/static/01a4a62b9eb69a808ae00f08d04dbfdc/5a791/images-8958.png 1248w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/01a4a62b9eb69a808ae00f08d04dbfdc/4ef49/images-8958.png",
                "alt": "Definition of dropWhile'",
                "title": "Definition of dropWhile'",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Definition of dropWhile'`}</figcaption>{`
  `}</figure></p>
    <p>{`Composing this with `}<inlineCode parentName="p">{`fst`}</inlineCode>{` gives us the normal `}<em parentName="p">{`dropWhile`}</em>{` because the changed list ends up in the first part of the resulting tuple. `}<inlineCode parentName="p">{`dropWhile p = fst . dropWhile' p`}</inlineCode></p>
    <p>{`This tupling technique is so powerful, we can redefine any `}<strong parentName="p">{`primitive recursion`}</strong>{` function in terms of fold. The approach is usually to make a sister function that tuples the result together with the original list and work on that.`}</p>
    <p>{`As far as I understand it, the idea is to essentially carry an extra argument into the fold so things are easier to work with. Which makes me wonder whether there's a monadic solution to this that produces cleaner code.`}</p>
    <p>{`The paper says that if you have a function like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "624px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/047e3797b5223d2d0a85394c0eb4efad/39c09/images-8959.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "22.421524663677133%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'90\\'%20viewBox=\\'0%200%20400%2090\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/047e3797b5223d2d0a85394c0eb4efad/ca0a1/images-8959.webp 223w", "/static/047e3797b5223d2d0a85394c0eb4efad/75680/images-8959.webp 445w", "/static/047e3797b5223d2d0a85394c0eb4efad/b5732/images-8959.webp 624w"],
              "sizes": "(max-width: 624px) 100vw, 624px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/047e3797b5223d2d0a85394c0eb4efad/e92b6/images-8959.png 223w", "/static/047e3797b5223d2d0a85394c0eb4efad/e66bf/images-8959.png 445w", "/static/047e3797b5223d2d0a85394c0eb4efad/39c09/images-8959.png 624w"],
              "sizes": "(max-width: 624px) 100vw, 624px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/047e3797b5223d2d0a85394c0eb4efad/39c09/images-8959.png",
              "alt": "images 8959",
              "title": "images 8959",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`You can `}<em parentName="p">{`fold`}</em>{` this, by first introducing an extra argument, `}<inlineCode parentName="p">{`y`}</inlineCode>{`, that is processed by a new function `}<inlineCode parentName="p">{`f`}</inlineCode>{` in the base case, but generally passed through without change. Then you redefine `}<inlineCode parentName="p">{`h y`}</inlineCode>{` with fold and introduce `}<inlineCode parentName="p">{`xs`}</inlineCode>{` as an extra argument to `}<inlineCode parentName="p">{`g`}</inlineCode>{`.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "846px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/fdf5f1dddee8326fe40f34639fad5259/5b481/images-8960.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "16.143497757847534%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'65\\'%20viewBox=\\'0%200%20400%2065\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/fdf5f1dddee8326fe40f34639fad5259/ca0a1/images-8960.webp 223w", "/static/fdf5f1dddee8326fe40f34639fad5259/75680/images-8960.webp 445w", "/static/fdf5f1dddee8326fe40f34639fad5259/47c32/images-8960.webp 846w"],
                "sizes": "(max-width: 846px) 100vw, 846px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/fdf5f1dddee8326fe40f34639fad5259/e92b6/images-8960.png 223w", "/static/fdf5f1dddee8326fe40f34639fad5259/e66bf/images-8960.png 445w", "/static/fdf5f1dddee8326fe40f34639fad5259/5b481/images-8960.png 846w"],
                "sizes": "(max-width: 846px) 100vw, 846px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/fdf5f1dddee8326fe40f34639fad5259/5b481/images-8960.png",
                "alt": "Primitive recursion",
                "title": "Primitive recursion",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Primitive recursion`}</figcaption>{`
  `}</figure></p>
    <p>{`This gives you primitive recursion, which isn't foldable unless you use the tupling technique.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "600px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/3ffd311b977b45e2117e172c0aca906a/0a47e/images-8961.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "12.556053811659194%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'49\\'%20viewBox=\\'0%200%20400%2049\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M257%2022l-2%203%202-1c2-3%203-2%202%202-2%205-1%208%202%209%203%200%204%201%201%203-2%203-4%202-3%200l-1-2-1%203c0%203%204%203%207%200%202-3%207-17%206-18-2-1-3%201-4%206-1%206-3%208-5%206v-5c2-7%200-10-4-6\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/3ffd311b977b45e2117e172c0aca906a/ca0a1/images-8961.webp 223w", "/static/3ffd311b977b45e2117e172c0aca906a/75680/images-8961.webp 445w", "/static/3ffd311b977b45e2117e172c0aca906a/e88ff/images-8961.webp 600w"],
              "sizes": "(max-width: 600px) 100vw, 600px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/3ffd311b977b45e2117e172c0aca906a/e92b6/images-8961.png 223w", "/static/3ffd311b977b45e2117e172c0aca906a/e66bf/images-8961.png 445w", "/static/3ffd311b977b45e2117e172c0aca906a/0a47e/images-8961.png 600w"],
              "sizes": "(max-width: 600px) 100vw, 600px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/3ffd311b977b45e2117e172c0aca906a/0a47e/images-8961.png",
              "alt": "images 8961",
              "title": "images 8961",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`And after the same sort of shenanigans as before, our function turns into a fold:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/cff1256bb96d29796d6470416d3ef91a/f3a19/images-8962.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "24.663677130044842%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'98\\'%20viewBox=\\'0%200%20400%2098\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/cff1256bb96d29796d6470416d3ef91a/ca0a1/images-8962.webp 223w", "/static/cff1256bb96d29796d6470416d3ef91a/75680/images-8962.webp 445w", "/static/cff1256bb96d29796d6470416d3ef91a/8d1ba/images-8962.webp 890w", "/static/cff1256bb96d29796d6470416d3ef91a/326b1/images-8962.webp 1086w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/cff1256bb96d29796d6470416d3ef91a/e92b6/images-8962.png 223w", "/static/cff1256bb96d29796d6470416d3ef91a/e66bf/images-8962.png 445w", "/static/cff1256bb96d29796d6470416d3ef91a/4ef49/images-8962.png 890w", "/static/cff1256bb96d29796d6470416d3ef91a/f3a19/images-8962.png 1086w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/cff1256bb96d29796d6470416d3ef91a/4ef49/images-8962.png",
                "alt": "Folded recursion",
                "title": "Folded recursion",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Folded recursion`}</figcaption>{`
  `}</figure></p>
    <p>{`Which lets us say that `}<inlineCode parentName="p">{`h y = fst . k y`}</inlineCode>{`, which is supposedly better than the definition we had before. I guess it is because we wanted to use `}<em parentName="p">{`fold`}</em>{` and now we are.`}</p>
    <h2 {...{
      "id": "using-fold-to-generate-functions"
    }}>{`Using fold to generate functions`}</h2>
    <p>{`Using a language with first-order functions lets us take the power of fold further still - we can generate functions. Similar to how you'd normally replace conses with functions, you can replace them with compositions.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "754px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/046ebc6e168e258a53df99990995521f/7527b/images-8963.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "14.798206278026903%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'59\\'%20viewBox=\\'0%200%20400%2059\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/046ebc6e168e258a53df99990995521f/ca0a1/images-8963.webp 223w", "/static/046ebc6e168e258a53df99990995521f/75680/images-8963.webp 445w", "/static/046ebc6e168e258a53df99990995521f/20eb0/images-8963.webp 754w"],
                "sizes": "(max-width: 754px) 100vw, 754px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/046ebc6e168e258a53df99990995521f/e92b6/images-8963.png 223w", "/static/046ebc6e168e258a53df99990995521f/e66bf/images-8963.png 445w", "/static/046ebc6e168e258a53df99990995521f/7527b/images-8963.png 754w"],
                "sizes": "(max-width: 754px) 100vw, 754px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/046ebc6e168e258a53df99990995521f/7527b/images-8963.png",
                "alt": "Compose as a fold",
                "title": "Compose as a fold",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Compose as a fold`}</figcaption>{`
  `}</figure></p>
    <p>{`But that's not very interesting. I'm going to skip over the paper's definitions of various `}<em parentName="p">{`suml`}</em>{` and `}<em parentName="p">{`foldl`}</em>{` and such functions and go straight to the interesting example - Ackermann's function.`}</p>
    <p>{`Ackermann's function is the first known example of a function that is computable, but isn't primitively recursive.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/bce991789e254f4b60f8c93f9d0e64ec/f43e4/images-8964.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "22.421524663677133%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'90\\'%20viewBox=\\'0%200%20400%2090\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/bce991789e254f4b60f8c93f9d0e64ec/ca0a1/images-8964.webp 223w", "/static/bce991789e254f4b60f8c93f9d0e64ec/75680/images-8964.webp 445w", "/static/bce991789e254f4b60f8c93f9d0e64ec/8d1ba/images-8964.webp 890w", "/static/bce991789e254f4b60f8c93f9d0e64ec/928c4/images-8964.webp 1120w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/bce991789e254f4b60f8c93f9d0e64ec/e92b6/images-8964.png 223w", "/static/bce991789e254f4b60f8c93f9d0e64ec/e66bf/images-8964.png 445w", "/static/bce991789e254f4b60f8c93f9d0e64ec/4ef49/images-8964.png 890w", "/static/bce991789e254f4b60f8c93f9d0e64ec/f43e4/images-8964.png 1120w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/bce991789e254f4b60f8c93f9d0e64ec/4ef49/images-8964.png",
                "alt": "Ackermann's function",
                "title": "Ackermann's function",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Ackermann's function`}</figcaption>{`
  `}</figure></p>
    <p>{`It's been converted to operate on lists rather than natural numbers so that `}<em parentName="p">{`n`}</em>{` is represented as a list of `}<em parentName="p">{`n`}</em>{` elements. The only reason we can do this, is that we're working with a higher order language.`}</p>
    <p>{`By appealing to the universal property, we find that `}<inlineCode parentName="p">{`ack = fold f v`}</inlineCode>{` is equivalent to these two equations:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "714px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/e8b41c4916733bd35835ab54b342ed6e/d67ca/images-8966.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "18.834080717488792%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'75\\'%20viewBox=\\'0%200%20400%2075\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/e8b41c4916733bd35835ab54b342ed6e/ca0a1/images-8966.webp 223w", "/static/e8b41c4916733bd35835ab54b342ed6e/75680/images-8966.webp 445w", "/static/e8b41c4916733bd35835ab54b342ed6e/80c40/images-8966.webp 714w"],
              "sizes": "(max-width: 714px) 100vw, 714px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/e8b41c4916733bd35835ab54b342ed6e/e92b6/images-8966.png 223w", "/static/e8b41c4916733bd35835ab54b342ed6e/e66bf/images-8966.png 445w", "/static/e8b41c4916733bd35835ab54b342ed6e/d67ca/images-8966.png 714w"],
              "sizes": "(max-width: 714px) 100vw, 714px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/e8b41c4916733bd35835ab54b342ed6e/d67ca/images-8966.png",
              "alt": "images 8966",
              "title": "images 8966",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`A simple calculation tells us that `}<inlineCode parentName="p">{`v = (1:)`}</inlineCode>{`, but we can't use the second equation the same way as before. We must first tackle `}<inlineCode parentName="p">{`ack (x:xs)`}</inlineCode>{` on the left-hand side.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/315c6c412a78a21c72e0096220607ccf/5819f/images-8967.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "13.452914798206278%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'55\\'%20viewBox=\\'0%200%20400%2055\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/315c6c412a78a21c72e0096220607ccf/ca0a1/images-8967.webp 223w", "/static/315c6c412a78a21c72e0096220607ccf/75680/images-8967.webp 445w", "/static/315c6c412a78a21c72e0096220607ccf/8d1ba/images-8967.webp 890w", "/static/315c6c412a78a21c72e0096220607ccf/20e4c/images-8967.webp 1042w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/315c6c412a78a21c72e0096220607ccf/e92b6/images-8967.png 223w", "/static/315c6c412a78a21c72e0096220607ccf/e66bf/images-8967.png 445w", "/static/315c6c412a78a21c72e0096220607ccf/4ef49/images-8967.png 890w", "/static/315c6c412a78a21c72e0096220607ccf/5819f/images-8967.png 1042w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/315c6c412a78a21c72e0096220607ccf/4ef49/images-8967.png",
                "alt": "ack (x:xs)",
                "title": "ack (x:xs)",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`ack (x:xs)`}</figcaption>{`
  `}</figure></p>
    <p>{`Now the first equation tells us that `}<inlineCode parentName="p">{`w = ack xs`}</inlineCode>{` and we normally apply the universal property on the second equation to get this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/79c648ec8550063247663b2d979296f1/11b93/images-8968.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "7.174887892376682%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'28\\'%20viewBox=\\'0%200%20400%2028\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/79c648ec8550063247663b2d979296f1/ca0a1/images-8968.webp 223w", "/static/79c648ec8550063247663b2d979296f1/75680/images-8968.webp 445w", "/static/79c648ec8550063247663b2d979296f1/8d1ba/images-8968.webp 890w", "/static/79c648ec8550063247663b2d979296f1/e9b15/images-8968.webp 1124w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/79c648ec8550063247663b2d979296f1/e92b6/images-8968.png 223w", "/static/79c648ec8550063247663b2d979296f1/e66bf/images-8968.png 445w", "/static/79c648ec8550063247663b2d979296f1/4ef49/images-8968.png 890w", "/static/79c648ec8550063247663b2d979296f1/11b93/images-8968.png 1124w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/79c648ec8550063247663b2d979296f1/4ef49/images-8968.png",
              "alt": "images 8968",
              "title": "images 8968",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`We can now calculate the definition of `}<inlineCode parentName="p">{`f`}</inlineCode>{`, which gives us the final folded ackerman's function:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/036aad1e6d5fd863ef9f734e58bac7da/cd536/images-8969.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "7.174887892376682%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'29\\'%20viewBox=\\'0%200%20400%2029\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/036aad1e6d5fd863ef9f734e58bac7da/ca0a1/images-8969.webp 223w", "/static/036aad1e6d5fd863ef9f734e58bac7da/75680/images-8969.webp 445w", "/static/036aad1e6d5fd863ef9f734e58bac7da/8d1ba/images-8969.webp 890w", "/static/036aad1e6d5fd863ef9f734e58bac7da/180af/images-8969.webp 1114w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/036aad1e6d5fd863ef9f734e58bac7da/e92b6/images-8969.png 223w", "/static/036aad1e6d5fd863ef9f734e58bac7da/e66bf/images-8969.png 445w", "/static/036aad1e6d5fd863ef9f734e58bac7da/4ef49/images-8969.png 890w", "/static/036aad1e6d5fd863ef9f734e58bac7da/cd536/images-8969.png 1114w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/036aad1e6d5fd863ef9f734e58bac7da/4ef49/images-8969.png",
              "alt": "images 8969",
              "title": "images 8969",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`It took using the universal property `}<em parentName="p">{`twice`}</em>{`, but we did it. We defined ackerman's function as a fold.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`The paper finishes with a survey of references for even more magical things you can do with folds. Using them for datatypes other than lists, performing monadic folds, redefining fold on relations, and how helpful fold can be in automatic program transformation.`}</p>
    <p>{`But all of that are topics for another day, today let's just agree that `}<em parentName="p">{`fold`}</em>{` is awesome and we should all be using it much more.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=cf076e67-d2c7-42cf-90b4-36d260742cba",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      